import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, ErrorState } from '@syncanix/ui';
import { useEffect, useRef } from 'react';

import { MyTenantsDocument } from '@/__generated__/graphql';
import { apolloClient } from '@/api/apollo';
import { setTenantHeaderGetter } from '@/api/auth';
import { queryClient } from '@/api/query-client';
import { isRealWorkspaceId, shouldResetTenantScope } from '@/api/tenant-binding';
import { BootSkeleton } from '@/components/BootSkeleton';
import { logger } from '@/lib/logger';

import { useT } from './I18nProvider';
import { useWorkspace } from './WorkspaceProvider';

import type { PropsWithChildren, ReactElement } from 'react';

/**
 * Split out of `Providers.tsx` when that file crossed the ~300-line ceiling
 * (`.claude/rules/architecture.md`), along a real seam rather than an arbitrary
 * one: this is a self-contained gate component that `Providers` already
 * exported, and it already had its own `WorkspaceHydrator.test.tsx` — so the
 * split makes the test file match its source, which is the convention that file
 * was named for. The edge is one-directional: `Providers` renders this; this
 * imports nothing from `Providers`.
 *
 * Hydrates `WorkspaceProvider` from the live tenant directory once the user is
 * authenticated, replacing the bootstrap seed, and installs the active-tenant
 * header getter into the Apollo link so subsequent requests carry the selected
 * tenant. Mounts inside both ApolloProvider (needs `useQuery`) and
 * WorkspaceProvider (needs `useWorkspace`).
 *
 * Three guarantees protect the tenant binding (live finding 2026-06-11: a
 * fresh browser session direct-loading a detail route rendered "Capability
 * not found" — its first-paint query went out under the bootstrap seed, the
 * server fell back to the personal tenant, and nothing ever refetched):
 *
 *   1. HYDRATION GATE — while authenticated and the bound tenant is not yet
 *      confirmed present in the hydrated directory, children don't mount, so no
 *      data query can fire under the wrong tenant. A boot-trusted session
 *      (persisted uuid) also holds at the skeleton until MyTenants confirms the
 *      id — opening the gate on the persisted id alone is what flashed the
 *      "your workspace" fallback title on cold load. A warm cache-and-network
 *      hit confirms the id synchronously, so returning users still paint
 *      instantly with no skeleton frame.
 *   2. UNREACHABLE → ERROR — if the directory query settles with no usable
 *      result (a DB outage, surfaced as a GraphQL `INTERNAL` error; a transport
 *      failure; or a settled-empty list), the gate shows ONE honest, retryable
 *      top-level error instead of failing open into a dashboard whose every
 *      panel errors independently under a mislabeled workspace.
 *   3. TENANT-CHANGE RESET — a mid-session workspace switch purges +
 *      refetches every active query (`shouldResetTenantScope`): the tenant
 *      travels as a header, so Apollo's cache can't tell tenants apart.
 *
 * The header getter reads a ref assigned during RENDER (not an effect) so a
 * child's render-time query can never observe a stale binding.
 */
export function WorkspaceHydrator({ children }: PropsWithChildren): ReactElement {
  const { isAuthenticated } = useAuth0();
  const { t } = useT();
  const { setWorkspaces, currentWorkspaceId } = useWorkspace();
  const { data, error, refetch } = useQuery(MyTenantsDocument, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const tenants = data?.myTenants;
    if (tenants && tenants.length > 0) {
      setWorkspaces(
        tenants.map((t) => ({
          id: t.id,
          name: t.name,
          slug: t.slug,
          isPersonal: t.isPersonal,
          accountId: t.accountId ?? null,
        })),
      );
    }
  }, [data, setWorkspaces]);

  const boundTenant = useRef<string | null>(null);
  boundTenant.current = currentWorkspaceId;
  useEffect(() => {
    setTenantHeaderGetter(() => boundTenant.current);
    return () => {
      setTenantHeaderGetter(null);
    };
  }, []);

  const lastBoundTenant = useRef<string | null>(null);
  useEffect(() => {
    const previous = lastBoundTenant.current;
    lastBoundTenant.current = currentWorkspaceId;
    if (shouldResetTenantScope(previous, currentWorkspaceId)) {
      // Purge BOTH caches so no surface keeps painting the previous
      // workspace's data under the new tenant header (A13). Apollo:
      // resetStore() clears + refetches active queries (the mounted page
      // immediately shows the new tenant). TanStack: clear() drops the
      // non-GraphQL cache; its queries refetch on next observe. The header
      // getter already serves the new id (assigned during render above).
      queryClient.clear();
      apolloClient.resetStore().catch((error: unknown) => {
        logger.warn('[workspace] apollo store reset after tenant change failed', { error });
      });
    }
  }, [currentWorkspaceId]);

  // Gate state derives from the settled query OUTCOME (`data` / `error`), never
  // Apollo's `loading` flag — the unskip re-render reports a spurious
  // loading=false mid-flight (observed live 2026-06-11). `boundTenantHydrated`
  // reads membership off `data` (not the provider's `workspaces`, which lags one
  // render behind the hydration effect above), so a warm cache hit paints a
  // returning user instantly with no skeleton frame or "your workspace" flash.
  const boundTenantHydrated =
    isRealWorkspaceId(currentWorkspaceId) &&
    (data?.myTenants ?? []).some((tenant) => tenant.id === currentWorkspaceId);

  // Unreachable = settled with no usable result: a DB outage (a GraphQL
  // `INTERNAL` error at HTTP 200 → `error` set, `data` undefined under
  // errorPolicy:'all'), a transport failure, or a settled-empty list (a fault
  // for a JWT user — the server always materializes the personal tenant first).
  const settledEmpty = data?.myTenants.length === 0;
  const directoryUnreachable = (error !== undefined && data === undefined) || settledEmpty;

  if (isAuthenticated && directoryUnreachable) {
    return (
      <ErrorState
        className="min-h-screen bg-background"
        title={t('common.error')}
        description={t('error.generic.body')}
        action={
          <Button
            variant="secondary"
            onClick={() => {
              // A still-failing transport retry rejects (GraphQL errors resolve
              // under errorPolicy:'all'); swallow it — the gate re-derives the
              // error surface from the settled query state.
              refetch().catch((retryError: unknown) => {
                logger.warn('[workspace] tenant directory retry failed', { error: retryError });
              });
            }}
          >
            {t('common.retry')}
          </Button>
        }
      />
    );
  }

  if (isAuthenticated && !boundTenantHydrated) {
    // Shared with the terms gate (ADR-0497). Both gates block the router,
    // at different depths, so React swaps one element for the other mid-load;
    // identical markup means the painted pixels never change and the two waits
    // read as one. See `BootSkeleton`'s docblock.
    return <BootSkeleton />;
  }

  return <>{children}</>;
}
